import { useState } from "react";

import type { DeploymentRole, DesktopBridge, DesktopSettings, DesktopSettingsPatch } from "./bridge.js";
import { DEFAULT_LAN_PORT } from "./bridge.js";
import { EmployeeManager } from "./EmployeeManager.js";
import { PrinterSelect } from "./PrinterSelect.js";
import { describeApiError } from "../domain/errors.js";
import "./desktop.css";

/**
 * First-run setup — the successor to nothing in the legacy app, which was
 * configured by editing files by hand. Runs on a fresh desktop install (see
 * `App.tsx`, gated on `setupComplete: false`) and walks the operator through
 * everything the app needs before it can be used:
 *
 *   1. **Role** — is this the computer that holds the data (host), or one that
 *      connects to another that does (LAN client)?
 *   2. **Printer** — which printer receipts go to (both roles print locally).
 *   3a. **host:** whether to serve the shop LAN, and behind what shared passphrase.
 *   3b. **client:** the hosting computer's address and that shared passphrase.
 *   4. **host only:** the employees whose PINs authorise saves at the counter.
 *
 * Finishing writes the settings in one go and marks setup complete; the desktop
 * shell (`apps/desktop/src/main.ts`) reacts — a client then connects to its host.
 */
type StepKey = "role" | "printer" | "host-lan" | "client-connect" | "employees";

function stepsFor(role: DeploymentRole): StepKey[] {
  return role === "host" ? ["role", "printer", "host-lan", "employees"] : ["role", "printer", "client-connect"];
}

export function SetupWizard({
  bridge,
  initial,
  persist,
}: {
  bridge: DesktopBridge;
  initial: DesktopSettings;
  /** Persists the finished settings (and keeps the cached copy in sync, which unmounts this wizard). */
  persist: (patch: DesktopSettingsPatch) => Promise<DesktopSettings>;
}) {
  const [role, setRole] = useState<DeploymentRole>(initial.role);
  const [printerName, setPrinterName] = useState<string | null>(initial.printerName);
  const [lanEnabled, setLanEnabled] = useState(initial.lan.enabled);
  const [lanPassphrase, setLanPassphrase] = useState(initial.lan.passphrase ?? "");
  const [remoteHost, setRemoteHost] = useState(initial.remote.host ?? "");
  const [remotePort, setRemotePort] = useState(initial.remote.port);
  const [remotePassphrase, setRemotePassphrase] = useState(initial.remote.passphrase ?? "");

  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = stepsFor(role);
  const step = steps[Math.min(stepIndex, steps.length - 1)]!;
  const isLast = stepIndex >= steps.length - 1;

  // A host serving the LAN must have a passphrase; a client must know where to connect.
  const lanBlocked = step === "host-lan" && lanEnabled && lanPassphrase.trim().length < 4;
  const connectBlocked = step === "client-connect" && (remoteHost.trim().length === 0 || remotePassphrase.trim().length === 0);
  const nextBlocked = lanBlocked || connectBlocked;

  async function finish() {
    setSaving(true);
    setError(null);
    try {
      await persist(
        role === "host"
          ? {
              setupComplete: true,
              role: "host",
              printerName,
              lan: { enabled: lanEnabled, passphrase: lanEnabled ? lanPassphrase.trim() : null },
            }
          : {
              setupComplete: true,
              role: "client",
              printerName,
              remote: { host: remoteHost.trim(), port: remotePort, passphrase: remotePassphrase.trim() },
            },
      );
      // On success the cached settings flip to `setupComplete: true` and this
      // wizard unmounts (host), or the shell navigates to the host (client) —
      // either way there is nothing more to do here.
    } catch (err) {
      setError(describeApiError(err));
      setSaving(false);
    }
  }

  function next() {
    if (isLast) void finish();
    else setStepIndex((i) => i + 1);
  }

  return (
    <main className="setup-page">
      <div className="setup-card">
        <header className="setup-header">
          <h1>Namestitev</h1>
          <ol className="setup-progress" aria-label="Koraki namestitve">
            {steps.map((key, i) => (
              <li key={key} className={i === stepIndex ? "current" : i < stepIndex ? "done" : ""} aria-current={i === stepIndex ? "step" : undefined} />
            ))}
          </ol>
        </header>

        <section className="setup-body">
          {step === "role" && (
            <fieldset className="setup-fieldset">
              <legend>Vloga tega računalnika</legend>
              <p className="setup-help">Ali ta računalnik hrani podatke, ali se poveže na drugega, ki jih hrani?</p>
              <label className="setup-choice">
                <input type="radio" name="role" checked={role === "host"} onChange={() => setRole("host")} />
                <span>
                  <strong>Glavni računalnik</strong>
                  <small>Hrani bazo. Lahko streže tudi drugim napravam v omrežju.</small>
                </span>
              </label>
              <label className="setup-choice">
                <input type="radio" name="role" checked={role === "client"} onChange={() => setRole("client")} />
                <span>
                  <strong>Omrežni odjemalec</strong>
                  <small>Poveže se na glavni računalnik v trgovini. Sam ne hrani podatkov.</small>
                </span>
              </label>
              <p className="setup-note">Potrdila lahko tiskata obe vlogi — vsak računalnik na svoj tiskalnik.</p>
            </fieldset>
          )}

          {step === "printer" && (
            <fieldset className="setup-fieldset">
              <legend>Tiskalnik</legend>
              <p className="setup-help">Na kateri tiskalnik naj se natisne potrdilo?</p>
              <label htmlFor="printer">Tiskalnik za potrdila</label>
              <PrinterSelect bridge={bridge} value={printerName} onChange={setPrinterName} />
            </fieldset>
          )}

          {step === "host-lan" && (
            <fieldset className="setup-fieldset">
              <legend>Omrežni dostop</legend>
              <p className="setup-help">Naj bodo podatki dostopni tudi drugim napravam v omrežju trgovine (telefoni, tablice, drugi računalniki)?</p>
              <label className="setup-toggle">
                <input type="checkbox" checked={lanEnabled} onChange={(e) => setLanEnabled(e.target.checked)} />
                <span>Streži tudi v omrežju</span>
              </label>
              {lanEnabled && (
                <>
                  <label htmlFor="lan-pass">Skupno geslo za omrežje</label>
                  <input id="lan-pass" type="password" value={lanPassphrase} onChange={(e) => setLanPassphrase(e.target.value)} autoComplete="new-password" />
                  <p className="setup-note">Vsaka naprava v omrežju bo morala vnesti to geslo. Blagajna sama (ta računalnik) ga ne potrebuje.</p>
                  {lanBlocked && <p className="settings-error">Geslo mora imeti vsaj 4 znake.</p>}
                </>
              )}
            </fieldset>
          )}

          {step === "client-connect" && (
            <fieldset className="setup-fieldset">
              <legend>Povezava na glavni računalnik</legend>
              <label htmlFor="remote-host">Naslov (IP) glavnega računalnika</label>
              <input id="remote-host" value={remoteHost} onChange={(e) => setRemoteHost(e.target.value)} placeholder="192.168.1.10" autoComplete="off" />
              <label htmlFor="remote-port">Vrata</label>
              <input
                id="remote-port"
                type="number"
                value={remotePort}
                onChange={(e) => setRemotePort(Number(e.target.value) || DEFAULT_LAN_PORT)}
                min={1}
                max={65535}
              />
              <label htmlFor="remote-pass">Skupno geslo za omrežje</label>
              <input id="remote-pass" type="password" value={remotePassphrase} onChange={(e) => setRemotePassphrase(e.target.value)} autoComplete="current-password" />
              <p className="setup-note">Naslov in geslo dobite na glavnem računalniku (Nastavitve → Omrežni dostop).</p>
            </fieldset>
          )}

          {step === "employees" && (
            <fieldset className="setup-fieldset">
              <legend>Zaposleni</legend>
              <p className="setup-help">Dodajte zaposlene. Njihov PIN potrjuje vsak prevzem in izdajo na blagajni.</p>
              <EmployeeManager />
            </fieldset>
          )}

          {error && (
            <p className="settings-error" role="alert">
              {error}
            </p>
          )}
        </section>

        <footer className="setup-footer">
          <button type="button" className="setup-back" onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={stepIndex === 0 || saving}>
            Nazaj
          </button>
          <button type="button" className="setup-next" onClick={next} disabled={nextBlocked || saving}>
            {saving ? "Shranjevanje …" : isLast ? "Zaključi" : "Naprej"}
          </button>
        </footer>
      </div>
    </main>
  );
}
